/**
 * Stats-Aggregator — berechnet Streak / Today / Accuracy aus dem ReviewLog.
 * Pure functions. Keine Storage-Logik.
 */
import type { ReviewLogEntry } from '$lib/store';

export interface UserStats {
  streak: number;
  todayDone: number;
  todayGoal: number;
  accuracy: number;
  totalReviews: number;
}

const DAY_MS = 86_400_000;

/** YYYY-MM-DD in lokaler Zeitzone. */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function reviewsByDay(log: readonly ReviewLogEntry[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const e of log) {
    const k = dayKey(new Date(e.reviewedAt));
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

/** Vorheriger Kalendertag (lokale Mitternacht) — DST-sicher, im Gegensatz zu
 * `cursor - 86_400_000ms` (das an 23-/25-h-Tagen einen Tag überspringt). */
export function previousLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
}

/** Aufeinanderfolgende Tage mit >=1 Review, vom heutigen Tag rückwärts. */
export function computeStreak(log: readonly ReviewLogEntry[], now: Date = new Date()): number {
  if (!log.length) return 0;
  const days = reviewsByDay(log);
  let streak = 0;
  let cursor = new Date(now);
  while (days.has(dayKey(cursor))) {
    streak++;
    cursor = previousLocalDay(cursor);
  }
  return streak;
}

export function todayDone(log: readonly ReviewLogEntry[], now: Date = new Date()): number {
  return reviewsByDay(log).get(dayKey(now)) ?? 0;
}

/**
 * Trefferquote = Recall-Erfolgsrate. In FSRS-Semantik ist nur "Again" (1) ein
 * vergessenes/fehlgeschlagenes Recall; "Hard" (2) wurde erinnert (Karte wird
 * nicht zurückgesetzt). Daher zählt rating >= 2 als Treffer.
 */
export function accuracy(log: readonly ReviewLogEntry[]): number {
  if (!log.length) return 0;
  const recalled = log.filter((e) => e.rating >= 2).length;
  return recalled / log.length;
}

export function userStats(
  log: readonly ReviewLogEntry[],
  todayGoal = 20,
  now: Date = new Date(),
): UserStats {
  return {
    streak: computeStreak(log, now),
    todayDone: todayDone(log, now),
    todayGoal,
    accuracy: accuracy(log),
    totalReviews: log.length,
  };
}

export interface DayCell {
  date: string;
  count: number;
}

/** Lückenlose Tagesserie der letzten `days` Tage (älteste zuerst) — Heatmap-Futter. */
export function dailyCounts(
  log: readonly ReviewLogEntry[],
  days: number,
  now: Date = new Date(),
): DayCell[] {
  const byDay = reviewsByDay(log);
  const out: DayCell[] = [];
  let cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = 0; i < days; i++) {
    const key = dayKey(cursor);
    out.unshift({ date: key, count: byDay.get(key) ?? 0 });
    cursor = previousLocalDay(cursor);
  }
  return out;
}

export interface DeckStat {
  deck: string;
  reviews: number;
  accuracy: number;
}

/**
 * Trefferquote je Deck (schwächstes zuerst) — speist die Drill-Empfehlung.
 * Reviews ohne Deck-Zuordnung (gelöschte Karten) werden ignoriert.
 */
export function deckAccuracy(
  log: readonly ReviewLogEntry[],
  deckOf: (cardId: string) => string | undefined,
): DeckStat[] {
  const agg = new Map<string, { total: number; hits: number }>();
  for (const e of log) {
    const deck = deckOf(e.cardId);
    if (!deck) continue;
    const a = agg.get(deck) ?? { total: 0, hits: 0 };
    a.total++;
    if (e.rating >= 2) a.hits++;
    agg.set(deck, a);
  }
  return [...agg.entries()]
    .map(([deck, a]) => ({ deck, reviews: a.total, accuracy: a.hits / a.total }))
    .sort((x, y) => x.accuracy - y.accuracy);
}

/** Tage bis zum Prüfungs-Termin. */
export function daysUntil(targetIso: string, now: Date = new Date()): number {
  const target = new Date(targetIso);
  return Math.ceil((target.getTime() - now.getTime()) / DAY_MS);
}

/**
 * Wandelt eine absolute Fälligkeit (dueMs) in ein kurzes Label um, relativ zu
 * `nowMs`. Speist die Rating-Buttons mit der ECHTEN nächsten Wiederholung
 * (siehe fsrs.previewIntervals) statt fester Platzhalter.
 */
export function dueHint(dueMs: number, nowMs: number = Date.now()): string {
  const diff = dueMs - nowMs;
  if (diff <= 0) return 'jetzt';
  const min = Math.round(diff / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.round(diff / 3_600_000);
  if (h < 24) return `${h} h`;
  const days = Math.round(diff / DAY_MS);
  return days === 1 ? 'morgen' : `${days} tage`;
}
